var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { computed, observable, makeObservable } from 'mobx';
import { Actor } from '../../../classes/actor/Actor';
import { memoize } from '../../../utils/mobx/decorators/memoize';
import { alias } from '../../../utils/serializr/schema/middle/alias';
import { primitive } from '../../../utils/serializr/schema/inner/primitive';
import { Wording } from '../Wording/Wording';
import { Solution } from '../Solution/Solution';
import { WordTaskPostfix } from './modules/WordTaskPostfix';
import { WordTaskPrefix } from './modules/WordTaskPrefix';
import { WordTaskEvaluate } from './modules/WordTaskEvaluate';
import { WordTaskReveal } from './modules/WordTaskReveal';
import { WordTaskActions } from './modules/WordTaskActions';
import { WordTaskNearest } from './modules/WordTaskNearest';
import { WordTaskInput } from './modules/WordTaskInput';
import { WordTaskExpect } from './modules/WordTaskExpect';
import { WordTaskMutability } from './modules/WordTaskMutability';
import { WordTaskForm } from './modules/WordTaskForm';
import { strip } from '../../../utils/serializr/schema/inner/strip';
import { editable } from '../../../utils/mobx/decorators/editable';
import { runtime } from '../../../classes/runtime/Runtime';
import { WordTaskUI } from './modules/WordTaskUI';
import { WordTaskProgress } from './modules/WordTaskProgress';
import { WordTaskInfo } from './modules/WordTaskInfo';
import { withDefault } from '../../../utils/serializr/schema/middle/withDefault';
import { WordTaskMask } from './modules/WordTaskMask';
export class WordTask extends Actor {
    type = 'wordtask';
    wordings = observable.array();
    solutions = observable.array();
    requiredAccuracy = 80; // required accuracy (percent between 0 and 100)
    isCaseSensitive = false;
    shouldShowAlternativesOnReveal = false;
    doesAlwaysRequireManualSend = false; // obsolete
    isRegex = false;
    isSufficientToContainWord = false;
    numberOfTries = 1;
    inputMask = '';
    currentAttempt = 1;
    constructor(init) {
        super(init);
        makeObservable(this);
    }
    get question() {
        return this.parent;
    }
    mutability = new WordTaskMutability(this);
    get wordTaskInput() {
        return new WordTaskInput(this);
    }
    get wordTaskExpect() {
        return new WordTaskExpect(this);
    }
    get wordTaskNearest() {
        return new WordTaskNearest(this);
    }
    get wordTaskEvaluate() {
        return new WordTaskEvaluate(this);
    }
    get taskProgress() {
        return new WordTaskProgress(this);
    }
    get wordTaskUI() {
        return new WordTaskUI(this);
    }
    get wordTaskPrefix() {
        return new WordTaskPrefix(this);
    }
    get wordTaskPostfix() {
        return new WordTaskPostfix(this);
    }
    get wordTaskInfo() {
        return new WordTaskInfo(this);
    }
    get wordTaskReveal() {
        return new WordTaskReveal(this);
    }
    get wordTaskActions() {
        return new WordTaskActions(this);
    }
    get wordTaskForm() {
        return new WordTaskForm(this);
    }
    get wordTaskMask() {
        return new WordTaskMask(this);
    }
    get wordTaskEdit() {
        return new runtime.availablePackages.Edit.classes.WordTaskEdit(this);
    }
    get isActiveWordTask() {
        return true;
    }
    get wordString() {
        return this.wordings.map(wording => wording.wordingString).join(';');
    }
    set wordString(s) {
        if (typeof s !== 'string')
            s = '';
        const wordings = s.split(';').map(s => new Wording({ parent: this, wordingString: s }));
        this.wordings.replace(wordings);
    }
    get solutionString() {
        if (this.solutions.length === 0)
            return '';
        return this.solutions.map(solution => solution.solutionString).join(';');
    }
    set solutionString(s) {
        if (s.length === 0) {
            if (this.solutions.length > 0) {
                this.solutions.replace([]);
            }
            return;
        }
        const solutions = s.split(';').map(s => new Solution({ parent: this, solutionString: s }));
        this.solutions.replace(solutions);
    }
    static get serializeInfo() {
        return {
            extends: Actor.serializeInfo,
            props: {
                wordString: alias('word', primitive),
                solutionString: alias('solution', primitive),
                inputMask: withDefault(primitive, ''),
            },
        };
    }
    static get stripInfo() {
        return {
            extends: Actor.stripInfo,
            props: {
                wordings: strip((task) => task.wordings.map(wording => ({ text: wording.wordingString }))),
                solutions: strip((task) => task.solutions.map(solution => ({ text: solution.solutionString }))),
                accuracy: strip((task) => task.requiredAccuracy),
                isCaseSensitive: strip((task) => task.isCaseSensitive),
                shouldShowAlternatives: strip((task) => task.shouldShowAlternativesOnReveal),
                isManualSend: strip((task) => task.doesAlwaysRequireManualSend),
                isRegex: strip((task) => task.isRegex),
            },
        };
    }
}
__decorate([
    editable('shallow')
], WordTask.prototype, "wordings", void 0);
__decorate([
    editable('shallow')
], WordTask.prototype, "solutions", void 0);
__decorate([
    editable()
], WordTask.prototype, "requiredAccuracy", void 0);
__decorate([
    editable()
], WordTask.prototype, "isCaseSensitive", void 0);
__decorate([
    editable()
], WordTask.prototype, "shouldShowAlternativesOnReveal", void 0);
__decorate([
    editable()
], WordTask.prototype, "doesAlwaysRequireManualSend", void 0);
__decorate([
    editable()
], WordTask.prototype, "isRegex", void 0);
__decorate([
    editable()
], WordTask.prototype, "isSufficientToContainWord", void 0);
__decorate([
    editable()
], WordTask.prototype, "numberOfTries", void 0);
__decorate([
    editable()
], WordTask.prototype, "inputMask", void 0);
__decorate([
    observable
], WordTask.prototype, "currentAttempt", void 0);
__decorate([
    memoize
], WordTask.prototype, "wordTaskInput", null);
__decorate([
    memoize
], WordTask.prototype, "wordTaskExpect", null);
__decorate([
    memoize
], WordTask.prototype, "wordTaskNearest", null);
__decorate([
    memoize
], WordTask.prototype, "wordTaskEvaluate", null);
__decorate([
    memoize
], WordTask.prototype, "taskProgress", null);
__decorate([
    memoize
], WordTask.prototype, "wordTaskUI", null);
__decorate([
    memoize
], WordTask.prototype, "wordTaskPrefix", null);
__decorate([
    memoize
], WordTask.prototype, "wordTaskPostfix", null);
__decorate([
    memoize
], WordTask.prototype, "wordTaskInfo", null);
__decorate([
    memoize
], WordTask.prototype, "wordTaskReveal", null);
__decorate([
    memoize
], WordTask.prototype, "wordTaskActions", null);
__decorate([
    memoize
], WordTask.prototype, "wordTaskForm", null);
__decorate([
    memoize
], WordTask.prototype, "wordTaskMask", null);
__decorate([
    memoize
], WordTask.prototype, "wordTaskEdit", null);
__decorate([
    computed
], WordTask.prototype, "wordString", null);
__decorate([
    computed
], WordTask.prototype, "solutionString", null);
__decorate([
    memoize
], WordTask, "serializeInfo", null);
__decorate([
    memoize
], WordTask, "stripInfo", null);
